import { ArrowSmallDownBtn } from '@sas/react-svg';
import { createElement, forwardRef, useCallback, useRef, useState } from 'react';
import { BaseMenuButtonProps, RenderButtonProps } from '.';
import { ReactKeyboardEvent } from '../../../react-synthetic-events';
import { DesignTokens, useDesignTokens } from '../../../theme';
import { setRef } from '../../../util/ref-util';
import { postfixTestId } from '../../../util/testId';
import { ButtonProps, ButtonVariant } from '../../Button';
import { BaseButton } from '../../Button/__internal__';
import { Icon } from '../../Icon';
import { BaseMenu, FocusLocation, getNextFocusableLocation } from '../../Menu/__internal__';
import { useToggleMenu } from '../useToggleMenu';

import cx from 'classnames';
import '../../Button/__internal__/BaseButton.css';
import cnBaseButton from '../../Button/__internal__/BaseButton.css.json';
import './BaseMenuButton.css';
import cn from './BaseMenuButton.css.json';

/**
 * @internal
 */
export const BaseMenuButton = forwardRef<HTMLElement, BaseMenuButtonProps>(function BaseMenuButton(
  {
    ariaLabel,
    children,
    icon,
    onItemSelected,
    onItemClosed,
    onDismiss,
    onOpen,
    onMouseDown,
    onMouseUp,
    onKeyDown,
    onKeyUp,
    parentWidth,
    renderButton,
    sections,
    hideDownArrow,
    menuAlign,
    menuDockingElement,
    testId,
    ...props
  },
  ref
) {
  const [defaultFocusLocation, setDefaultFocusLocation] = useState<FocusLocation>();

  const buttonRef = useRef<HTMLElement | null>(null);

  const { handleDismiss, handleMouseDown, handlePress, setMenuIsOpen, menuIsOpen } = useToggleMenu({
    disabled: props.disabled,
    onDismiss: () => {
      buttonRef.current?.focus();
      onDismiss?.();
    },
    onOpen: () => {
      setDefaultFocusLocation(undefined);
      onOpen?.();
    },
  });

  const handleItemSelected = (indices: [number, number][], key?: string) => {
    buttonRef.current?.focus();
    onItemSelected?.(indices, key);
    setMenuIsOpen(false);
    onDismiss?.();
  };

  const setButtonRef = useCallback(
    (button: HTMLElement | null) => {
      buttonRef.current = button;
      setRef(ref, button);
    },
    [ref]
  );

  const isButtonKeyDown = useRef(false);

  const handleKeyUp = useCallback(
    (e: ReactKeyboardEvent<HTMLElement>) => {
      if (props.disabled) return;

      if (!isButtonKeyDown.current) {
        return;
      }

      isButtonKeyDown.current = false;

      onKeyUp?.(e);

      let focusLocation;
      if (e.key === 'Enter' || e.key === ' ') {
        focusLocation = getNextFocusableLocation(-1, sections, false);
      } else if (e.altKey && e.key === 'ArrowDown') {
        focusLocation = getNextFocusableLocation(-1, sections, false);
      } else if (e.altKey && e.key === 'ArrowUp') {
        focusLocation = getNextFocusableLocation(0, sections, true);
      }

      if (focusLocation !== undefined) {
        e.preventDefault();
        setDefaultFocusLocation(focusLocation);
        setMenuIsOpen(true);
        onOpen?.();
        // prevents further propagation of the current event in the capturing and bubbling phases.
        // it fixes the issue "focus move to another example in Storybook"
        e.stopPropagation();
      }
    },
    [onKeyUp, onOpen, props.disabled, sections, setMenuIsOpen]
  );

  const dt = useDesignTokens();

  const buttonProps: RenderButtonProps = {
    ...props,
    ariaLabel: ariaLabel ?? props.text,
    icon,
    children:
      hideDownArrow ?? !!icon ? null : (
        <Icon
          icon={ArrowSmallDownBtn}
          className={cx(cnBaseButton.iconInner, cn.icon)}
          tooltip=""
          ariaHidden
        />
      ),
    onMouseDown: (e) => {
      handleMouseDown(e);
      onMouseDown?.(e);
    },
    onMouseUp,
    onClick: handlePress,
    onKeyDown: (e) => {
      if (isButtonKeyDown.current) {
        return;
      }

      isButtonKeyDown.current = true;

      if (e.altKey && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
        e.stopPropagation();
      }
      onKeyDown?.(e);
    },
    onKeyUp: (e) => {
      handleKeyUp(e);
    },
  };

  return (
    <div
      data-testid={testId}
      className={props.className ? `${props.className}-container` : undefined}
    >
      {renderButton ? (
        renderButton({ ...buttonProps, menuIsOpen, setButtonRef })
      ) : (
        <BaseButton
          tokens={() => getMenuButtonTokens(menuIsOpen, buttonProps.variant, props.text, dt)}
          {...buttonProps}
          icon={
            buttonProps.icon
              ? {
                  ...buttonProps.icon,
                  tokens:
                    menuIsOpen && buttonProps.variant === ButtonVariant.PrimaryIcon
                      ? () => {
                          switch (dt.themeVariant) {
                            case 'dark':
                              return { fg: dt.c.colorTextPrimary };
                            case 'high-contrast':
                              return { fg: dt.statePrimaryNova.selected.fg };
                            default:
                              return { fg: dt.c.colorTextInverse };
                          }
                        }
                      : null,
                }
              : undefined
          }
          ref={setButtonRef}
          testId={postfixTestId(testId, 'button')}
          aria-haspopup
        />
      )}
      <BaseMenu
        testId={postfixTestId(testId, 'menu')}
        isOpen={menuIsOpen}
        sections={sections}
        position={{ dockingElement: menuDockingElement ?? buttonRef.current, ...menuAlign }}
        onItemSelected={handleItemSelected}
        onItemClosed={onItemClosed}
        onDismiss={handleDismiss}
        defaultFocusLocation={defaultFocusLocation}
      />
    </div>
  );
});

/**
 * @internal
 */
export const getMenuButtonTokens = (
  menuIsOpen: boolean,
  variant: ButtonProps['variant'],
  text: string | undefined,
  dt: DesignTokens
) => {
  const defaultTokens =
    menuIsOpen && variant !== ButtonVariant.PrimaryLight
      ? {
          ...dt.stateStandardNova.active,
          bd: text ? dt.stateStandardNova.active.bd : 'transparent',
        }
      : {};
  switch (dt.themeVariant) {
    case 'dark':
      return menuIsOpen &&
        (variant === ButtonVariant.Primary ||
          variant === ButtonVariant.PrimaryIcon ||
          variant === ButtonVariant.PrimarySmall ||
          variant === ButtonVariant.PrimaryLight)
        ? dt.statePrimaryNova.selected
        : defaultTokens;
    case 'high-contrast':
      return menuIsOpen &&
        (variant === ButtonVariant.Primary ||
          variant === ButtonVariant.PrimaryIcon ||
          variant === ButtonVariant.PrimarySmall ||
          variant === ButtonVariant.PrimaryLight)
        ? dt.statePrimaryNova.selected
        : defaultTokens;
    default:
      return menuIsOpen &&
        (variant === ButtonVariant.Primary ||
          variant === ButtonVariant.PrimaryIcon ||
          variant === ButtonVariant.PrimarySmall ||
          variant === ButtonVariant.PrimaryLight)
        ? {
            ...dt.statePrimaryNova.selected,
            fg: dt.c.colorTextInverse,
          }
        : defaultTokens;
  }
};
